import React, { useEffect, useRef, useState } from "react"
import types from "prop-types"
import clsx from "clsx"
import { isEmpty, sortBy } from "lodash-es"
import { withFormsy } from "formsy-react"
import InputErrorMessage from "shared/InputErrorMessage"
import MentionableTextInputMenu from "./MentionableTextInputMenu"
import { MENU_ITEM_IDENTIFYING_ATTRIBUTE } from "./MentionableTextInputMenuItem"

const MENTION_TRIGGER = "@"

const mentionableUsers = ({ cursorPosition, users, value }) => {
  const textBeforeCaret = value.slice(0, cursorPosition).toLowerCase()
  const lastMentionTriggerIndex = textBeforeCaret.lastIndexOf(MENTION_TRIGGER)

  if (lastMentionTriggerIndex === -1) return []

  let filteredUsers
  const partialNameToMatch = textBeforeCaret.slice(lastMentionTriggerIndex + 1, cursorPosition)

  if (partialNameToMatch) {
    filteredUsers = users.filter((user) => user.fullName.toLowerCase().includes(partialNameToMatch))
  } else {
    filteredUsers = users
  }

  return sortBy(filteredUsers, ["lastName", "firstName"])
}

const MentionableTextInput = ({
  className = "",
  disabled = false,
  errorMessages = [],
  hasBaseInputStyle = true,
  isPristine = true,
  name = undefined,
  onChange,
  onChangeSideEffect = () => {},
  placeholder = "",
  required = false,
  rows = "1",
  users = [],
  uuid = undefined,
  value = "",
}) => {
  const textareaRef = useRef()

  const [mentionables, setMentionables] = useState([])
  const [cursorPosition, setCursorPosition] = useState(value?.length || 0)

  const updateValue = ({ target }) => {
    const { value: newValue } = target
    const newCursorPosition = textareaRef.current?.selectionStart ?? 0

    setMentionables(mentionableUsers({
      cursorPosition: newCursorPosition,
      users,
      value: newValue,
    }))

    setCursorPosition(newCursorPosition)
    onChangeSideEffect(newValue)
    onChange(newValue)
  }

  useEffect(() => {
    if (!textareaRef.current) return
    if (document.activeElement !== textareaRef.current) return

    textareaRef.current.setSelectionRange(cursorPosition, cursorPosition)
  }, [cursorPosition, value])

  const clearMentionables = () => setMentionables([])

  const insertMentionable = (mentionableIndex) => {
    const mentionable = mentionables[mentionableIndex]

    if (!mentionable) {
      clearMentionables()
      return
    }

    const textBeforeCaret = value.slice(0, cursorPosition)
    const mentionTriggerIndex = textBeforeCaret.lastIndexOf(MENTION_TRIGGER)

    if (mentionTriggerIndex === -1) {
      clearMentionables()
      return
    }

    const textBeforeMention = value.slice(0, mentionTriggerIndex) || " "
    const mention = `${MENTION_TRIGGER}${mentionable.fullName}`
    const textAfterCaret = value.slice(cursorPosition)

    let newValue = `${textBeforeMention}${mention}`
    const newCursorPosition = newValue.length

    newValue += textAfterCaret

    setCursorPosition(newCursorPosition)
    onChangeSideEffect(newValue)
    onChange(newValue)
    clearMentionables()
    textareaRef.current.focus()
  }

  const onBlur = (event) => {
    const { relatedTarget } = event
    const relatedTargetIsMenuItem = relatedTarget?.getAttribute(MENU_ITEM_IDENTIFYING_ATTRIBUTE) === "true"

    if (!relatedTargetIsMenuItem) clearMentionables()
  }

  return (
    <div className="relative">
      <div
        className={clsx(className, "grow-wrap multiline-text-input")}
        data-replicated-value={value}
      >
        <textarea
          className={clsx(hasBaseInputStyle && "base-input")}
          style={{ fontSize: "inherit", lineHeight: "inherit", backgroundColor: "inherit" }}
          id={uuid}
          name={name || uuid}
          type="text"
          disabled={disabled}
          value={value}
          rows={rows}
          placeholder={`${placeholder} ${required ? "*" : ""}`}
          ref={textareaRef}
          onChange={updateValue}
          onBlur={onBlur}
        />
      </div>
      <InputErrorMessage isPristine={isPristine} errorMessages={errorMessages} />
      {
        !isEmpty(mentionables) && (
          <MentionableTextInputMenu
            clearMentionables={clearMentionables}
            insertMentionable={insertMentionable}
            mentionables={mentionables}
            textareaRef={textareaRef}
          />
        )
      }
    </div>
  )
}

MentionableTextInput.propTypes = {
  className: types.string,
  disabled: types.bool,
  errorMessages: types.arrayOf(types.string),
  hasBaseInputStyle: types.bool,
  isPristine: types.bool,
  name: types.string,
  onChange: types.func.isRequired,
  onChangeSideEffect: types.func,
  placeholder: types.string,
  required: types.bool,
  rows: types.string,
  users: types.arrayOf(types.shape({
    fullName: types.string.isRequired,
    id: types.number.isRequired,
  })),
  uuid: types.string,
  value: types.string,
}

export const FormsyMentionableTextInput = withFormsy(({ setValue, ...rest }) => (
  <MentionableTextInput
    onChange={(value) => setValue(value)}
    {...rest}
  />
))

FormsyMentionableTextInput.displayName = "FormsyMentionableTextInput"

export default MentionableTextInput
